import { useEffect, useRef, useState } from 'react';
import { Camera, Loader2, X } from 'lucide-react';
import { api, withAuthToken } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { hapticNotify } from '@/lib/telegram';
import { cn } from '@/lib/cn';

interface Props {
  /** Backend POST URL (relative to /api/v1). */
  uploadUrl: string;
  currentUrl?: string | null;
  onUploaded?: (url: string) => void;
  label?: string;
  className?: string;
}

export const PhotoUploader = ({ uploadUrl, currentUrl, onUploaded, label, className }: Props) => {
  const ref = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<string | null>(currentUrl ?? null);
  const [error, setError] = useState<string | null>(null);
  const blobUrlRef = useRef<string | null>(null);

  const cleanupBlob = () => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
  };

  useEffect(() => () => cleanupBlob(), []);

  const onPick = async (file: File) => {
    setError(null);
    if (!file.type.startsWith('image/')) {
      setError('Только изображения');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('Макс 10 МБ');
      return;
    }
    setBusy(true);
    cleanupBlob();
    const blob = URL.createObjectURL(file);
    blobUrlRef.current = blob;
    setPreview(blob);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post(uploadUrl, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = data.url ?? data.photo_url ?? data.receipt_url ?? data.url;
      if (url) {
        setPreview(url);
        onUploaded?.(url);
      }
      hapticNotify('success');
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Ошибка загрузки');
      hapticNotify('error');
    } finally {
      setBusy(false);
      // Server URL took over; release the blob.
      cleanupBlob();
    }
  };

  // Local blob URLs (blob:) and remote http(s) URLs render as-is.
  // Server-side /uploads URLs need the auth token appended.
  const fullUrl = preview
    ? preview.startsWith('blob:') || preview.startsWith('http')
      ? preview
      : withAuthToken(preview)
    : null;

  return (
    <div className={cn('space-y-2', className)}>
      {label && <div className="text-sm font-medium">{label}</div>}
      <div
        className={cn(
          'relative flex h-32 cursor-pointer items-center justify-center overflow-hidden rounded-xl border-2 border-dashed bg-muted/40',
          fullUrl && 'border-solid border-border',
        )}
        onClick={() => ref.current?.click()}
      >
        {fullUrl ? (
          <img src={fullUrl} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="flex flex-col items-center gap-1 text-muted-foreground">
            <Camera className="h-6 w-6" />
            <span className="text-xs">Добавить фото</span>
          </div>
        )}
        {busy && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/70">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        )}
        {fullUrl && !busy && (
          <Button
            type="button"
            variant="secondary"
            size="icon"
            className="absolute right-2 top-2 h-7 w-7 shadow"
            onClick={(e) => {
              e.stopPropagation();
              setPreview(null);
              onUploaded?.('');
            }}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
      <input
        ref={ref}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onPick(f);
          e.target.value = '';
        }}
      />
      {error && <div className="text-xs text-destructive">{error}</div>}
    </div>
  );
};
